import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const { currentUser, userProfile, resetPassword } = useAuth();
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  useEffect(() => {
    if (currentUser && userProfile) {
      if (userProfile.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [currentUser, navigate, userProfile]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await resetPassword(email);
      setEmailSent(true);
      addNotification('success', 'Email de reinitialisation envoye');
    } catch (error) {
      let errorMessage = 'Impossible d envoyer l email de reinitialisation';

      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Aucun compte n est associe a cette adresse email';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Adresse email invalide';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Trop de tentatives. Reessayez plus tard';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }

      addNotification('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 opacity-30 blur-lg" />
              <div className="relative rounded-2xl border border-gray-100 bg-white p-4 shadow-xl" />
            </div>
          </div>
          <h1 className="mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-4xl font-bold text-transparent">
            Mot de passe oublié
          </h1>
          <p className="text-sm text-gray-500">
            Entrez votre adresse email pour recevoir un lien de reinitialisation
          </p>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-white/20 bg-white/80 p-8 shadow-2xl backdrop-blur-sm">
          <div className="absolute right-0 top-0 h-32 w-32 translate-x-16 -translate-y-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 opacity-50" />
          <div className="absolute bottom-0 left-0 h-24 w-24 -translate-x-12 translate-y-12 rounded-full bg-gradient-to-tr from-indigo-100 to-pink-100 opacity-50" />

          <div className="relative z-10">
            {emailSent ? (
              <div className="space-y-6 text-center">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-5">
                  <p className="text-sm font-semibold text-emerald-800">Email envoye</p>
                  <p className="mt-2 text-sm text-emerald-700">
                    Si cette adresse existe dans le systeme, un lien de reinitialisation a ete envoye.
                  </p>
                </div>

                <Link
                  to="/login"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 px-6 py-4 text-base font-semibold text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Retour a la connexion
                </Link>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
                    Adresse email
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="block w-full rounded-xl border border-gray-200 bg-gray-50/50 py-4 pl-10 pr-3 transition-all duration-200 hover:bg-white focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full justify-center rounded-xl border border-transparent bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? <LoadingSpinner size="sm" /> : 'Envoyer le lien'}
                </button>

                <div className="text-center">
                  <div className="mb-3 flex items-center justify-center gap-4 text-xs font-medium text-gray-500">
                    <Link to="/privacy-policy" className="transition hover:text-blue-600">
                      Politique de confidentialite
                    </Link>
                    <Link to="/terms-of-service" className="transition hover:text-blue-600">
                      Conditions d utilisation
                    </Link>
                  </div>
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 transition hover:text-blue-700"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Retour a la connexion
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
